/**
 * Responsible initiating form field behaviors and sending events down to any
 * behaviors or extended functionality.
 * @module formFieldBehavior
 */
import property from 'lodash/property';
import Symbol from 'es6-symbol';
import registerJQueryPlugin, { noopActions } from '../../lib/register-jquery-plugin';

/**
 * Defines default options for form field behaviors
 * @type {Object}
 */
const FORM_FIELD_BEHAVIOR_OPTIONS = {
  labelElement: 'label',
  fieldElements: ['input', 'select', 'textarea'],
  behaviors: [],
};

/**
 * Defines the different types of supported events.
 * @type {Object}
 */
export const EVENTS = {
  FOCUS: 'focus',
  BLUR: 'blur',
  INVALID: 'invalid',
  INPUT: 'input',
  CHANGE: 'change',
  LOAD: 'load',
  // This is a nonstandard event, but it does provide clarity when an element
  // was no longer but in its current state it is valid
  VALID: 'valid',
};

/**
 * Defines the types associated to the elements within form field behaviors. It
 * uses Symbol as the value so that unique values can be used without caring
 * about conflicts values. Think of this similar to an Enum or an IOTA.
 * @enum
 * @type {Object}
 */
export const TYPES = {
  CONTAINER: Symbol('container'),
  LABEL: Symbol('label'),
  FIELD: Symbol('field'),
};

/**
 * Creates a FIELD based on the block and optionally supplied element.
 * @private
 * @param {Symbol} type - Uniquely identify a type of FIELD
 * @param {HTMLElement} container - Contains the child elements
 * @param {string} block - The block name
 * @param {string|null} [element=null] - The element name
 * @returns {Object} The name and node representing the FIELD.
 */
const createField = (type, container, block, element = null) => {
  if (element) {
    const name = `${block}__${element}`;
    return { type, name, node: container.querySelector(`.${name}`) };
  }

  return { type, name: block, node: container };
};

/**
 * Negotiates and resolves the form FIELD within the CONTAINER. There are a
 * number of possible element names that can be used, so this will ensure that
 * the supported element names correlate to what is in the DOM.
 * @private
 * @param {HTMLElement} container - Contains the form FIELD element
 * @param {string} block - The block name
 * @param {Array} fieldElements = Possible elements names
 * @returns {Object} The name and node representing the form FIELD.
 */
const getFormField = (container, block, fieldElements) => {
  const [field, ...otherFields] = fieldElements
    .map(element => createField(TYPES.FIELD, container, block, element))
    .filter(({ node }) => node);

  if (otherFields.length) {
    throw new Error('Form field behaviors only supports one field in a container');
  }

  if (!field) {
    throw new Error('No form field node found in form field behaviors');
  }

  return field;
};

/**
 * Provides additional data that can be used from the consumer side of the
 * form field behavior. Think of this as extra data that isn't part of the
 * event.
 * @private
 * @param {HTMLElement} fieldElement - The form field element
 * @returns {Object} Data to be passed along to the event.
 */
const buildAdditionalData = (fieldElement) => {
  const { validateOnlyOnSubmit } = property('form.dataset')(fieldElement) || {};
  return {
    validateOnlyOnSubmit: !!validateOnlyOnSubmit,
  };
};

/**
 * Creates an event listener and passes back all the necessary properties
 * needed for clean up.
 * @private
 * @param {string} eventName - The name of the event
 * @param {HTMLElement} node - Element receiving the event
 * @param {Array} behaviors - The listeners or behaviors provided from the outside
 * @param {Function} [fn=e => e] - Allow for internal hooking in of the callback
 * @returns {Object} Properties containing event information
 */
const addListener = (eventName, node, behaviors, fn = e => e) => {
  let handler;
  const data = buildAdditionalData(node);
  node.addEventListener(eventName, handler = (e) => {
    fn(e);
    behaviors.forEach(listener => listener && listener(eventName, e, data));
  });

  return { node, eventName, handler };
};

/**
 * Removes all events applied to an instance of form field behaviors.
 * @private
 * @param {Object} listeners - Info about each element
 */
const removeListeners = (listeners) => {
  listeners.forEach((listener) => {
    if (!listener) {
      return;
    }

    const { node, eventName, handler } = listener;
    node.removeEventListener(eventName, handler);
  });
};

/**
 * Fires off a load event denoting that the element has loaded.
 * @private
 * @param {HTMLElement} fieldNode - The element receiving the event
 */
const fireLoadEvent = (fieldNode) => {
  const evt = new CustomEvent(EVENTS.LOAD);
  fieldNode.dispatchEvent(evt);
};

/**
 * Responsible for handling any select specific events.
 * @param {HTMLElement} fieldNode - Element receiving the event
 * @param {Array} listeners - Called on when event has been fired off
 * @returns {Function|null} Event handler or null depending on if the event
 * will be fired off
 */
const addChangeListener = (fieldNode, listeners) => {
  if (fieldNode.tagName.toLowerCase() === 'select') {
    return addListener(EVENTS.CHANGE, fieldNode, listeners);
  }

  return null;
};

/**
 * Responsible for setting up any behavior that needs to hook into form field
 * behaviors. The goal of this work is to:
 *
 * - Offload responsibility of the interaction so any form field behavior
 *   functionality can scale horizontally.
 * - Decrease the amount of event bound to the DOM by calling on the listener
 *   provided by the behavior.
 * - Consolidate all events for ease of clean up.
 *
 * As part of the setup, a number of requirements are needed for a form field
 * behavior include:
 *
 * - A CONTAINER element (the one passed in)
 * - A LABEL element
 * - Some kinda of form FIELD element
 *
 * Once all the requirements have been met, form field behaviors will interact
 * with "behaviors", which serve the following purposes:
 *
 * - Accept the output of elements
 * - Respond to any events dictated by form field behaviors by forwarding along
 *   the event name followed by the event on the form field
 *
 * To do so, a behavior is a function where the returned function acts as the
 * responder. In it's simplest form, a behavior is a function that returns a
 * function, where the first function passes along all the "nodes" or elements
 * defined in TYPES and the second function (or inner function) it used for
 * listening to/responding to events.
 *
 * @example
 * const myBehavior = (blockElement, labelElement, fieldFormElement) => (type, evt) => {
 *   switch(type) {
 *     case 'FOCUS':
 *       blockElement.classList.add('i-do-focus-things');
 *     case 'BLUR':
 *       blockElement.classList.remove('i-do-focus-things');
 *       blockElement.classList.add('i-do-blur-things');
 *   };
 * };
 *
 * formFieldBehaviors($el, 'some-block', {
 *   behaviors: [myBehavior]
 * });
 *
 * @see TYPES
 * @see FORM_FIELD_BEHAVIOR_OPTIONS
 * @param {HTMLElement} container - The element containing the LABEL and FIELD
 * @param {string} block - Name of top level block
 * @param {string} labelElement - Name of label element associated with the block
 * @param {Object} options - Options used for overriding
 * @returns {Object} Properties used for interacting after the initial call
 */
export default function formFieldBehaviors(container, block, options = {}) {
  const {
    labelElement,
    fieldElements,
    behaviors,
    deregister = () => {},
  } = Object.assign({}, FORM_FIELD_BEHAVIOR_OPTIONS, options);
  const formField = getFormField(container, block, fieldElements);
  const label = createField(TYPES.LABEL, container, block, labelElement);

  if (!label.node) {
    throw new Error(`No field found in a form field behavior for ${label.name}`);
  }

  const elements = [
    createField(TYPES.CONTAINER, container, block),
    label,
    formField,
  ];

  const { FOCUS, BLUR, INVALID, INPUT, LOAD, VALID } = EVENTS;
  const listeners = behaviors.map(behavior => behavior(...elements));
  const { node: fieldNode } = formField;
  const focusListener = addListener(FOCUS, fieldNode, listeners, ({ target }) => target.setCustomValidity(''));
  const blurListener = addListener(BLUR, fieldNode, listeners);
  const invalidListener = addListener(INVALID, fieldNode, listeners);
  const inputListener = addListener(INPUT, fieldNode, listeners);
  const loadListener = addListener(LOAD, fieldNode, listeners);
  const changeListener = addChangeListener(fieldNode, listeners);
  const validListener = addListener(VALID, fieldNode, listeners);
  const eventListeners = [
    focusListener,
    blurListener,
    invalidListener,
    inputListener,
    loadListener,
    changeListener,
    validListener,
  ];

  // At this point all the necessary elements are in the DOM so we communicate
  // to any subscribers that the form field behavior functionality has loaded.
  fireLoadEvent(fieldNode);

  return {
    removeListeners: () => {
      deregister();
      removeListeners(eventListeners);
    },
  };
}

/**
 * Responsible for providing default implementaiton of update options.
 * @param {HTMLElement} el - Element receiving form field behaviors
 * @param {Object} options - Configurable options
 */
export const defaultUpdateOptions = (el, options) => options;

/**
 * Responsible for bootstrapping together jQuery plugin at runtime.
 * @param {string} block - Name of block being referenced
 * @param {Array} behaviors - Any implemented behaviors.
 * @param {*} updateOptions - Called on prior to make modifications to options
 * or element
 * @returns {Function} Called on when plugin is called on
 */
const bootstrapJQueryPlugin = (
  block,
  behaviors,
  updateOptions,
) => (
  el,
  options = {},
  { deregister } = noopActions,
) => {
  const { behaviors: pluginBehaviors = [] } = options;
  const combinedBehaviors = behaviors.concat(pluginBehaviors);
  const opts = Object.assign({
    formFieldBehaviors,
    behaviors: combinedBehaviors,
    deregister,
  }, options);
  const { formFieldBehaviors: formFieldBehaviorsFn } = opts;
  const nextOptions = updateOptions(el, opts);
  return formFieldBehaviorsFn(el, block, nextOptions);
};

/**
 * Responsible for creating a jQuery plugin from a form field behaviors
 * instance. Think of this has a jQuery plugin adapter for form field behaviors.
 * @param {string} pluginName - Name of plugin to create
 * @param {string} block - Name of block being referenced
 * @param {Array} behaviors - Any implemented behaviors.
 * @param {Function} [updateOptions=defaultUpdateOptions] - Called on so that any
 * dynamic options can be set at runtime.
 */
export const createJQueryPlugin = (
  pluginName,
  block,
  behaviors,
  updateOptions = defaultUpdateOptions,
) =>
  registerJQueryPlugin(pluginName, bootstrapJQueryPlugin(block, behaviors, updateOptions));



// WEBPACK FOOTER //
// ./styleguide/src/assets/elements/fields/form-field-behaviors.js